import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useStripe, useElements, PaymentElement, PaymentRequestButtonElement } from '@stripe/react-stripe-js';

import { Form } from 'react-form';
import * as queryString from 'query-string';
import PropTypes, { any } from 'prop-types';

import moment from 'moment';
import requireAuth from '../hocs/requireAuth';
import { required } from '../../utils/validation';

import * as checkoutEventRegistrationApi from '../../api/checkoutEventRegistrationApi';

import { getApiAxiosInstance } from '../../utils/network';
import { interpretStripeCardError } from '../../utils/payments';
import { eventFlows, registrationUserOptions, regStepIds } from '../../utils/eventConstants';
import { getUsapMessageFromError } from '../../utils/usap';

import paymentsConstants from '../../../constants/payments';

import { gtag } from '../Analytics/gtag';
import { Input } from '../Form';
import PaymentMethodList from '../PaymentMethodList';
import { showErrorPopup } from '../../actions/notification-actions';
import { trackEvent } from '../Analytics/TrackedClick';

const EventRegistrationCheckoutFormLegacy = (props) => {
    const {
        checkoutRequest,
        setCheckoutSubmittingFlag,
        submitting,
        currencySymbol,
        eventId,
        eventFee,
        auth,
        isAddingCardPayment,
        savedPaymentMethods,
        toggleSplashBallLoader,
        setPaymentTitle,
        sidebarFlow,
        setSwitchApiResponse,
        multipleDivisionRegistrations,
        getMultiRegistrationTotal,
        registrationTypeForCamps,
        handleCloseClick,
     } = props;

    const { paymentTitles } = paymentsConstants;

    const dispatch = useDispatch();
    const stripe = useStripe();
    const elements = useElements();
    const [loadError, setLoadError] = useState(null);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    const [isUsingCheckoutForm, setUsingCheckoutForm] = useState(false);
    const [makeDefaultCard, setMakeDefaultCard] = useState(null);
    const [paymentRequest, setPaymentRequest] = useState(null);
    const [showDefaultCardPaymentPage, setShowDefaultCardPayment] = useState(true);
    const [stripePaymentError, setStripePaymentError] = useState(null);

    const parsed = queryString.parse(props.location.search);
    const { jwt } = parsed;
    const isSwitchFlow = sidebarFlow === regStepIds.DIVISIONS_SWITCH;
    const isCampFlow = sidebarFlow === eventFlows.CAMP_FLOW;

    const setDefaultPaymentMethod = () => {
        if (!!savedPaymentMethods && savedPaymentMethods.length > 0) {
            const defaultPaymentMethod = savedPaymentMethods.find((card) => card.isDefaultPaymentMethod && !card.isExpired);
            setSelectedPaymentMethod(defaultPaymentMethod);
            if (defaultPaymentMethod) {
                setPaymentTitle(paymentTitles.make_payment);
            } else {
                setMakeDefaultCard(true);
                setShowDefaultCardPayment(false);
                setPaymentTitle(paymentTitles.select_method);
            }
        } else if (typeof (setPaymentTitle) === 'function') {
            setPaymentTitle(paymentTitles.select_method);
        }
    };

    useEffect(() => {
        setDefaultPaymentMethod();
    }, [savedPaymentMethods]);

    useEffect(() => {
        initializePaymentRequest();
    }, [stripe]);

    const displayTotalFee = () => {
        if (isCampFlow) {
            return getMultiRegistrationTotal();
        }
            return `${currencySymbol}${eventFee > 0 && eventFee?.toFixed(2)}`;
    };

    const validateCardholderName = (value) => {
        const incompleteError = !required(value) ? '' : 'You must provide a name.';
        return incompleteError;
    };

    const eventRegistrationAndPayment = async (paymentRequestResult = null) => {
        let res;
        let paymentIntentId;

        checkoutRequest.isSaveCardFeatureEnabled = false;
        checkoutRequest.isSavingCard = false;

        const axiosInstance = getApiAxiosInstance(jwt);
        try {
            if (isSwitchFlow) {
                res = await checkoutEventRegistrationApi.legacySwitchDivisions(axiosInstance, eventId, checkoutRequest);
                ({ paymentIntentId } = res.data.newDivisionRegistration);
                setSwitchApiResponse(res.data);
            } else if (isCampFlow) {
                // Mapping the camp flow data into a new object
                const mappedCampFlowData = {
                    // Creating a new key `childDivisionRegistrationRequests` with a transformed array
                    childDivisionRegistrationRequests: Object.entries(multipleDivisionRegistrations)
                    // Filtering out entries where quantity is greater than 0 since we filter the now new array  of [ [key, value]]
                    .filter(([, value]) => value.quantity > 0)
                    // Flattening and mapping the filtered entries
                    .flatMap(([key, value]) => value.playerNames.map(playerName => ({
                        tmsEventDivisionId: Number(key), // Convert `tmsEventDivisionId` from string to number
                        playerName, // Assign `playerName` from the array
                        }))),
                };
                res = await checkoutEventRegistrationApi.legacyMultiRegistration(axiosInstance, eventId, mappedCampFlowData);
                ({ clientSecret: paymentIntentId } = res.data);
            } else {
                res = await checkoutEventRegistrationApi.legacyMemberRegistrationCheckout(axiosInstance, eventId, checkoutRequest);
                ({ paymentIntentId } = res.data);
            }
        } catch (error) {
            handleCloseClick();

            const defaultMessage = 'Error completing registration';
            const retMessage = (error?.response?.data?.Message?.indexOf('suspended') > -1) ? error.response.data.Message : defaultMessage;
            const usapValidationMessage = getUsapMessageFromError(error);
            const retError = error?.response?.data?.DisplayMessage || usapValidationMessage || error?.response?.data?.Message || defaultMessage;

            trackEvent(`eventRegistrationAndPayment`, {
                page: 'EventRegistrationCheckoutFormLegacy',
                errorMessage: retMessage,
                action: `Error-${retError}`,
                memberId: auth.user.id,
                eventId,
                client: 'Web',
                paymentIntentId,
                timestamp: moment.utc(),
            });

            dispatch(showErrorPopup({
                message: retMessage,
                error: retError,
            }));

            if (paymentRequestResult) {
                paymentRequestResult.complete('fail');
            }

            // ... why was this still executing? lmao
            setCheckoutSubmittingFlag(false);
            return;
        }

        const { error: stripeCardError } = await stripe.confirmPayment({
            elements,
            clientSecret: paymentIntentId,
            confirmParams: {
                // This is a required parameter for the confirmPayment method, but we don't need it for our use case at the moment
                return_url: `https://example.com/order/123/complete`,
            },
            redirect: 'if_required',
        });

        if (stripeCardError) {
            const errorMessage = interpretStripeCardError(stripeCardError);

            trackEvent(`stripeCardError`, {
                page: 'EventRegistrationCheckoutFormLegacy',
                stripeCardError,
                action: `Error-${errorMessage}`,
                memberId: auth.user.id,
                eventId,
                client: 'Web',
                paymentIntentId,
                timestamp: moment.utc(),
            });

            setCheckoutSubmittingFlag(false);
            setStripePaymentError(errorMessage);
            return;
        }

        if (paymentRequestResult) {
            paymentRequestResult.complete('success');
        }

        props.stripePaymentSuccessWrapUp(res.data);
        setCheckoutSubmittingFlag(false);
    };

    const handleSubmit = async () => {
        setStripePaymentError(null);
        // added selectedPaymentMethod condition not to return if paying by saved card
        if (!selectedPaymentMethod && (!stripe || !elements)) {
            // Stripe.js has not loaded yet. Make sure to disable
            // form submission until Stripe.js has loaded.
            return;
        }
        // Trigger form validation and wallet collection
        const { error: submitError } = await elements.submit();

        if (submitError) {
            // Stripe element handles displaying the error, so we don't need to do anything here
            return;
        }

        setCheckoutSubmittingFlag(true);
        gtag('event', {
            category: 'Payment Info Entered',
            context: `eventId: ${eventId}`,
        });
        // adding track calls to check impact of save card feature - mapped to GA4
        if (window && window.analytics) {
            const { MULTIPLE_PLAYERS, SINGLE_PLAYER } = registrationUserOptions;
            const registrationType = registrationTypeForCamps === MULTIPLE_PLAYERS ? MULTIPLE_PLAYERS : SINGLE_PLAYER;

            window.analytics.track('Payment Info Entered', {
                userId: auth.user.id,
                eventId,
                registrationType,
            });
        }
        // creating a private function because same code is required for apple/google pay
        await eventRegistrationAndPayment(null);
    };
    // function to select to use new card
    const handleAddNewCard = () => {
        if (!submitting) {
            setUsingCheckoutForm(true);
            setSelectedPaymentMethod(null);
            props.setPaymentTitle(paymentTitles.none);
        }
    };
    // function to change the saved card
    const changePaymentMethod = () => {
        if (!submitting) {
            setShowDefaultCardPayment(false);
            props.setPaymentTitle(paymentTitles.select_method);
        }
    };
    // select new card from saved cards
    const selectPaymentMethod = (card) => {
        if (!submitting && !card.isExpired) {
            setSelectedPaymentMethod(card);
        }
    };

    const handlePaymentMethodReceived = async (paymentRequestResult) => {
        checkoutRequest.paymentMethodId = paymentRequestResult.paymentMethod.id;
        await eventRegistrationAndPayment(paymentRequestResult);
    };

    const checkAllSavedCardsExpired = () => {
        const expiredCards = savedPaymentMethods.filter(card => card.isExpired);
        return expiredCards.length === savedPaymentMethods.length;
    };

    const initializePaymentRequest = async () => {
        if (stripe && !isAddingCardPayment) {
            const paymentRequestInstance = stripe.paymentRequest({
                country: props.paymentProviderCountry.isoalpha2,
                currency: props.currencyAbbreviation?.toLowerCase(),
                total: {
                    label: 'Total',
                    amount: eventFee * 100,
                },
                requestPayerName: true,
                requestPayerEmail: true,
            });
            toggleSplashBallLoader();
            // Check the availability of the Payment Request API.
            await paymentRequestInstance.canMakePayment().then(result => {
                if (result) {
                    paymentRequestInstance.on('paymentmethod', handlePaymentMethodReceived);
                    setPaymentRequest(paymentRequestInstance);
                } else if (checkAllSavedCardsExpired()) { // if no wallet activated and no saved card
                    setUsingCheckoutForm(true);
                    setPaymentTitle(paymentTitles.none);
                }
            });
            toggleSplashBallLoader();
        }
    };

    const walletPaymentButton = () => {
        if (paymentRequest && showDefaultCardPaymentPage) {
            const paymentButtonOptions = {
                paymentRequest,
                style: {
                    paymentRequestButton: {
                        type: 'default',
                        theme: 'dark',
                        borderRadius: '24px',
                        width: '99%',
                    },
                },
            };
            return (<><PaymentRequestButtonElement options={paymentButtonOptions} /><hr className="hr-text" data-content="OR" /></>);
        }
        return <></>;
    };

    const handleOnPaymentElementLoadError = (e) => {
        setLoadError(e?.error?.message);
    };

    const StripeLoadError = () => {
        return (
            <div className="payment-error-container">
                <p className="text-bold text-dark mb16">Payment system temporarily unavailable</p>
                <p>We are having trouble loading our payment form. Please try:</p>
                <ul>
                    <li>Refreshing the page</li>
                    <li>Checking your internet connection</li>
                    <li>Trying again in a few moments</li>
                </ul>
                <button
                    className="btn btn-xl btn-primary text-uppercase font-weight-normal w-100 mt16 mb16"
                    type="button"
                    onClick={() => window.location.reload()}
                >
                    Reload Page
                </button>
                <p className="error-text">
                    Error: {loadError || 'Unknown payment processing error'}
                </p>
            </div>
        );
    };

    const showPaymentMethodList = false;

    return (
        <div>
            {loadError ? (
                <StripeLoadError />
            ) : (
                <Form onSubmit={handleSubmit}>
                    {(formApi) => {
                        const { submitForm } = formApi;
                        return (
                            <form onSubmit={submitForm} id="EventRegistrationCheckoutFormLegacy">
                                {showPaymentMethodList ? (
                                    <>
                                        <PaymentMethodList
                                            cardList={savedPaymentMethods}
                                            selectedPaymentMethod={selectedPaymentMethod}
                                            handleAddNewCard={handleAddNewCard}
                                            changePaymentMethod={changePaymentMethod}
                                            selectPaymentMethod={selectPaymentMethod}
                                            isUsedForPayment={true}
                                            currencySymbol={currencySymbol}
                                            eventFee={eventFee}
                                            submitting={submitting}
                                            stripe={stripe}
                                            showDefaultCardPaymentPage={showDefaultCardPaymentPage}
                                            walletPaymentButton={walletPaymentButton}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <div>
                                            <div className={`row`}>
                                                <Input
                                                    id={`cardholder-name`}
                                                    field="name"
                                                    label={`Cardholder Name`}
                                                    formGroupCssClass="col-12"
                                                    placeholder="ex. Serena Williams"
                                                    validate={value => validateCardholderName(value)}
                                                    disableProfanityCheck={true}
                                                />
                                            </div>
                                            <div className={`row`}>
                                                <div className={`form-group form-group-xl col-12 mb16`}>
                                                    <PaymentElement
                                                        options={{
                                                            layout: 'auto',
                                                            paymentMethodOrder: ['card'],
                                                            wallets: {
                                                                googlePay: 'never',
                                                                applePay: 'never',
                                                            },
                                                        }}
                                                        onLoadError={handleOnPaymentElementLoadError}
                                                    />
                                                </div>
                                                {stripePaymentError && (
                                                    <div className="pl16 pr16 mb16">
                                                        <p className="error-text">
                                                            {stripePaymentError}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <button
                                                type="submit"
                                                className={`btn btn-xl btn-primary text-uppercase font-weight-normal w-100 ${!stripe || submitting ? 'text-dark' : ''}`}
                                                disabled={!stripe || submitting}
                                            >
                                                {/* adding isAddingCardPayment condition to show Add or Pay text and hide amount */}
                                                {submitting && (<i className="fas fa-circle-notch fa-spin" />)} {isAddingCardPayment ? 'Save' : 'Pay'} {displayTotalFee()}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </form>
                        );
                    }}
                </Form>
            )}
        </div>
    );
};

EventRegistrationCheckoutFormLegacy.propTypes = {
    location: PropTypes.objectOf(any).isRequired,
    sidebarFlow: PropTypes.string,
    setSwitchApiResponse: PropTypes.func.isRequired,
    getMultiRegistrationTotal: PropTypes.func.isRequired,
    multipleDivisionRegistrations: PropTypes.objectOf(any).isRequired,
    registrationTypeForCamps: PropTypes.string.isRequired,
    eventFee: PropTypes.number.isRequired,
};

EventRegistrationCheckoutFormLegacy.defaultProps = {
    sidebarFlow: '',
};

export default requireAuth(EventRegistrationCheckoutFormLegacy);
